
import { withStyles } from '@material-ui/styles';
import React, { PureComponent } from 'react';

import { translate } from '../../../../base/i18n';
import { IconMicrophoneEmpty, IconVolumeEmpty } from '../../../../base/icons';
import MeetHourJS from '../../../../base/lib-meet-hour';
import { connect, equals } from '../../../../base/redux';
import Checkbox from '../../../../base/ui/components/web/Checkbox';
import { createLocalAudioTracks } from '../../../functions';

import AudioSettingsHeader from './AudioSettingsHeader';
import MicrophoneEntry from './MicrophoneEntry';
import SpeakerEntry from './SpeakerEntry';
import { useAudioContentStyles } from './styles';

const browser = MeetHourJS.util.browser;


export type Props = {

  /**
   * The deviceId of the microphone in use.
   */
  currentMicDeviceId: string,

  /**
   * The deviceId of the output device in use.
   */
  currentOutputDeviceId: string,

  /**
   * Used to set a new microphone as the current one.
   */
  setAudioInputDevice: Function,

  /**
   * Used to set a new output device as the current one.
   */
  setAudioOutputDevice: Function,

  /**
   * A list of objects containing the labels and deviceIds
   * of all the output devices.
   */
  outputDevices: Object[],

  /**
   * A list with objects containing the labels and deviceIds
   * of all the input devices.
   */
  microphoneDevices: Object[],

  /**
   * Invoked to obtain translated strings.
   */
  t: Function,

  /**
   * Whether noise suppression is enabled or not.
   */
  noiseSuppressionEnabled: boolean,

  /**
   * Handler to toggle noise suppression.
   */
  onToggleNoiseSuppression: Function,

  /**
   * The classes object to apply styles.
   */
  classes?: Object,

  /**
   * User preferred microphone device ID.
   */
  userSelectedMicDeviceId?: string,

  /**
   * User preferred microphone label.
   */
  userSelectedMicDeviceLabel?: string,

  /**
   * User preferred audio output device ID.
   */
  userSelectedAudioOutputDeviceId?: string,

  /**
   * User preferred audio output device label.
   */
  userSelectedAudioOutputDeviceLabel?: string,
};

type State = {

  /**
   * An list of objects, each containing the microphone label, audio track, device id
   * and track error if the case.
   */
  audioTracks: Object[],
};

/**
 * Implements a React {@link Component} which displayes a list of all
 * the audio input & output devices to choose from.
 *
 * @augments Component
 */
class AudioSettingsContent extends PureComponent<Props, State> {
    _componentWasUnmounted: boolean;

    /**
   * Initializes a new {@code AudioSettingsContent} instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   */
    constructor(props) {
        super(props);

        this._onMicrophoneEntryClick = this._onMicrophoneEntryClick.bind(this);
        this._onSpeakerEntryClick = this._onSpeakerEntryClick.bind(this);

        this.state = {
            audioTracks: props.microphoneDevices.map(({ deviceId, label }) => {
                return {
                    deviceId,
                    hasError: false,
                    mhTrack: null,
                    label
                };
            })
        };
    }

    _onMicrophoneEntryClick: (string) => void;

    /**
   * Click handler for the microphone entries.
   *
   * @param {string} deviceId - The deviceId for the clicked microphone.
   * @returns {void}
   */
    _onMicrophoneEntryClick(deviceId) {
        this.props.setAudioInputDevice(deviceId);
    }

    _onSpeakerEntryClick: (string) => void;

    /**
   * Click handler for the speaker entries.
   *
   * @param {string} deviceId - The deviceId for the clicked speaker.
   * @returns {void}
   */
    _onSpeakerEntryClick(deviceId) {
        this.props.setAudioOutputDevice(deviceId);
    }

    /**
     * Determines which microphone device is currently selected.
     *
     * @returns {string|undefined}
     */
    _getSelectedMicDeviceId() {
        const { currentMicDeviceId, userSelectedMicDeviceId, userSelectedMicDeviceLabel } = this.props;
        const devices = this.state.audioTracks.length > 0
            ? this.state.audioTracks
            : this.props.microphoneDevices;

        if (!devices || devices.length === 0) {
            return undefined;
        }

        // 1. Check if currentMicDeviceId matches an available device
        if (currentMicDeviceId) {
            const match = devices.find(d => d.deviceId === currentMicDeviceId);

            if (match) {
                return match.deviceId;
            }
        }

        // 2. Check if userSelectedMicDeviceId matches an available device
        if (userSelectedMicDeviceId) {
            const match = devices.find(d => d.deviceId === userSelectedMicDeviceId);

            if (match) {
                return match.deviceId;
            }
        }

        // 3. If current or userSelected was 'default', try finding the default device
        if (currentMicDeviceId === 'default' || userSelectedMicDeviceId === 'default') {
            const defaultDev = devices.find(d => d.deviceId === 'default');

            if (defaultDev) {
                return defaultDev.deviceId;
            }
        }

        // 4. Try matching on label if available
        if (userSelectedMicDeviceLabel) {
            const match = devices.find(d => d.label && (
                d.label === userSelectedMicDeviceLabel
                || userSelectedMicDeviceLabel.includes(d.label)
                || d.label.includes(userSelectedMicDeviceLabel)
            ));

            if (match) {
                return match.deviceId;
            }
        }

        // 5. Fallback: device with 'default' ID or the first available device
        const defaultDevice = devices.find(d => d.deviceId === 'default');

        return defaultDevice ? defaultDevice.deviceId : devices[0].deviceId;
    }

    /**
     * Determines which speaker device is currently selected.
     *
     * @returns {string|undefined}
     */
    _getSelectedOutputDeviceId() {
        const {
            currentOutputDeviceId,
            outputDevices,
            userSelectedAudioOutputDeviceId,
            userSelectedAudioOutputDeviceLabel
        } = this.props;

        if (!outputDevices || outputDevices.length === 0) {
            return undefined;
        }

        // 1. Check if currentOutputDeviceId matches an available device
        if (currentOutputDeviceId) {
            const match = outputDevices.find(d => d.deviceId === currentOutputDeviceId);

            if (match) {
                return match.deviceId;
            }
        }

        // 2. Check if userSelectedAudioOutputDeviceId matches an available device
        if (userSelectedAudioOutputDeviceId) {
            const match = outputDevices.find(d => d.deviceId === userSelectedAudioOutputDeviceId);

            if (match) {
                return match.deviceId;
            }
        }

        // 3. If current or userSelected was 'default', try finding the default device
        if (currentOutputDeviceId === 'default' || userSelectedAudioOutputDeviceId === 'default') {
            const defaultDev = outputDevices.find(d => d.deviceId === 'default');

            if (defaultDev) {
                return defaultDev.deviceId;
            }
        }

        // 4. Try matching on label if available
        if (userSelectedAudioOutputDeviceLabel) {
            const match = outputDevices.find(d => d.label && (
                d.label === userSelectedAudioOutputDeviceLabel
                || userSelectedAudioOutputDeviceLabel.includes(d.label)
                || d.label.includes(userSelectedAudioOutputDeviceLabel)
            ));

            if (match) {
                return match.deviceId;
            }
        }

        // 5. Fallback: device with 'default' ID or the first available device
        const defaultDevice = outputDevices.find(d => d.deviceId === 'default');

        return defaultDevice ? defaultDevice.deviceId : outputDevices[0].deviceId;
    }

    /**
   * Renders a single microphone entry.
   *
   * @param {Object} data - An object with the deviceId, mhTrack & label of the microphone.
   * @param {number} index - The index of the element, used for creating a key.
   * @param {string} selectedMicId - The deviceId of the currently selected microphone.
   * @returns {React$Node}
   */
    _renderMicrophoneEntry(data, index, selectedMicId) {
        const { deviceId, label, mhTrack, hasError } = data;
        const isSelected = deviceId === selectedMicId;

        return (
            <MicrophoneEntry
                deviceId = { deviceId }
                hasError = { hasError }
                isSelected = { isSelected }
                key = { `me-${index}` }
                mhTrack = { mhTrack }
                onClick = { this._onMicrophoneEntryClick }>
                {label}
            </MicrophoneEntry>
        );
    }

    /**
   * Renders a single speaker entry.
   *
   * @param {Object} data - An object with the deviceId and label of the speaker.
   * @param {number} index - The index of the element, used for creating a key.
   * @param {string} selectedSpeakerId - The deviceId of the currently selected speaker.
   * @returns {React$Node}
   */
    _renderSpeakerEntry(data, index, selectedSpeakerId) {
        const { deviceId, label } = data;
        const key = `se-${index}`;
        const isSelected = deviceId === selectedSpeakerId;

        return (
            <SpeakerEntry
                deviceId = { deviceId }
                isSelected = { isSelected }
                key = { key }
                onClick = { this._onSpeakerEntryClick }>
                {label}
            </SpeakerEntry>
        );
    }

    /**
   * Creates and updates the audio tracks.
   *
   * @returns {void}
   */
    async _setTracks() {
        if (browser.isWebKitBased()) {
            // It appears that at the time of this writing, creating audio tracks blocks the browser's main thread for
            // long time on safari. Wasn't able to confirm which part of track creation does the blocking exactly, but
            // not creating the tracks seems to help and makes the UI much more responsive.
            return;
        }

        this._disposeTracks(this.state.audioTracks);

        const audioTracks = await createLocalAudioTracks(
      this.props.microphoneDevices,
      5000
        );

        if (this._componentWasUnmounted) {
            this._disposeTracks(audioTracks);
        } else {
            this.setState({
                audioTracks
            });
        }
    }

    /**
   * Disposes the audio tracks.
   *
   * @param {Object[]} audioTracks - An array of objects with the mhTrack to be disposed.
   * @returns {void}
   */
    _disposeTracks(audioTracks) {
        audioTracks.forEach(({ mhTrack }) => {
            mhTrack && mhTrack.dispose();
        });
    }

    /**
   * Implements React's {@link Component#componentDidMount}.
   *
   * @inheritdoc
   */
    componentDidMount() {
        this._setTracks();
        this._checkAndSyncDefaultDevices();
    }

    /**
   * Implements React's {@link Component#componentWillUnmount}.
   *
   * @inheritdoc
   */
    componentWillUnmount() {
        this._componentWasUnmounted = true;
        this._disposeTracks(this.state.audioTracks);
    }

    /**
   * Implements React's {@link Component#componentDidUpdate}.
   *
   * @inheritdoc
   */
    componentDidUpdate(prevProps) {
        if (!equals(this.props.microphoneDevices, prevProps.microphoneDevices)) {
            this._setTracks();
        }

        if (!equals(this.props.microphoneDevices, prevProps.microphoneDevices)
            || !equals(this.props.outputDevices, prevProps.outputDevices)) {
            this._checkAndSyncDefaultDevices();
        }
    }

    /**
     * Checks and synchronizes default devices if no valid device is currently selected.
     *
     * @returns {void}
     */
    _checkAndSyncDefaultDevices() {
        const {
            currentMicDeviceId,
            currentOutputDeviceId,
            setAudioInputDevice,
            setAudioOutputDevice
        } = this.props;

        // Skip the empty-deviceId placeholders enumerateDevices() returns before permission is granted; selecting one
        // just re-runs a getUserMedia that the browser has already denied.
        const microphoneDevices = this.props.microphoneDevices?.filter(d => d.deviceId);
        const outputDevices = this.props.outputDevices?.filter(d => d.deviceId);

        if (microphoneDevices && microphoneDevices.length > 0) {
            const isCurrentMicAvailable = microphoneDevices.some(
                d => d.deviceId === currentMicDeviceId
            );

            if (!isCurrentMicAvailable && !currentMicDeviceId) {
                const defaultMic = microphoneDevices.find(d => d.deviceId === 'default') || microphoneDevices[0];

                setAudioInputDevice(defaultMic.deviceId);
            }
        }

        if (outputDevices && outputDevices.length > 0) {
            const isCurrentSpeakerAvailable = outputDevices.some(
                d => d.deviceId === currentOutputDeviceId
            );

            if (!isCurrentSpeakerAvailable && !currentOutputDeviceId) {
                const defaultSpeaker = outputDevices.find(d => d.deviceId === 'default') || outputDevices[0];

                setAudioOutputDevice(defaultSpeaker.deviceId);
            }
        }
    }

    /**
   * Implements React's {@link Component#render}.
   *
   * @inheritdoc
   */
    render() {
        const { classes, outputDevices, t } = this.props;
        const selectedMicId = this._getSelectedMicDeviceId();
        const selectedSpeakerId = this._getSelectedOutputDeviceId();


        return (
            <div>
                <div className = 'audio-preview-content'>
                    <AudioSettingsHeader
                        IconComponent = { IconMicrophoneEmpty }
                        text = { t('settings.microphones') } />
                    {this.state.audioTracks.map((data, i) =>
                        this._renderMicrophoneEntry(data, i, selectedMicId)
                    )}
                    {outputDevices.length > 0 && (
                        <>
                            <hr className = 'audio-preview-hr' />
                            <AudioSettingsHeader
                                IconComponent = { IconVolumeEmpty }
                                text = { t('settings.speakers') } />
                        </>
                    )}
                    {outputDevices.map((data, i) => this._renderSpeakerEntry(data, i, selectedSpeakerId))}
                    <hr className = 'audio-preview-hr' />
                    <div className = { classes.noiseSuppressionContainer }>
                        <Checkbox
                            checked = { this.props.noiseSuppressionEnabled || false }
                            className = 'custom-checkbox'
                            label = { <span className = { classes.noiseSuppressionLabel }>{t('toolbar.enableNoiseSuppression')}</span> }
                            name = 'noise-suppression-checkbox'
                            onChange = { this.props.onToggleNoiseSuppression } />
                    </div>
                </div>
            </div>
        );
    }
}


const mapStateToProps = state => {
    const settings = state['features/base/settings'];

    return {
        userSelectedMicDeviceId: settings?.userSelectedMicDeviceId,
        userSelectedAudioOutputDeviceId: settings?.userSelectedAudioOutputDeviceId,
        userSelectedMicDeviceLabel: settings?.userSelectedMicDeviceLabel,
        userSelectedAudioOutputDeviceLabel: settings?.userSelectedAudioOutputDeviceLabel
    };
};

export default translate(
    connect(mapStateToProps)(withStyles(useAudioContentStyles)(AudioSettingsContent))
);
